import type { MockSectionCustomerInput } from './mockSectionData.util';
import { mockSectionData } from './mockSectionData.util';
import {
  buildWhatsPossibleAtMaturityCopy,
  buildWhatsPossibleAtMaturityHeadline,
  buildWhatsPossibleAtMaturityMetrics,
  buildWhatsPossibleAtMaturityRoiSubtitle,
  buildWhatsPossibleAtMaturitySource,
  WHATS_POSSIBLE_AT_MATURITY_BENCHMARK_SOURCE,
  type WhatsPossibleAtMaturityMetric,
} from './whatsPossibleAtMaturitySnapshot.util';

const isBenchmarkSnapshot = (snapshot: Record<string, unknown>): boolean =>
  snapshot.snapshotSource === WHATS_POSSIBLE_AT_MATURITY_BENCHMARK_SOURCE;

const readMetrics = (rows: unknown): WhatsPossibleAtMaturityMetric[] =>
  Array.isArray(rows) ? (rows as WhatsPossibleAtMaturityMetric[]) : [];

/**
 * Normalizes whats-possible-at-maturity snapshots with benchmark metrics and copy.
 * @param snapshot - Stored or live snapshot JSON.
 * @param customer - Customer context for mock fallback fields.
 * @param reportingPeriod - Report period label.
 * @returns Snapshot with OBH Wisconsin benchmark framing.
 */
export const normalizeWhatsPossibleAtMaturitySnapshot = (
  snapshot: Record<string, unknown>,
  customer: MockSectionCustomerInput,
  reportingPeriod: string,
): Record<string, unknown> => {
  const mockBase = mockSectionData(
    'whats-possible-at-maturity',
    customer,
    reportingPeriod,
  );
  const isBenchmark = isBenchmarkSnapshot(snapshot);
  const metrics = readMetrics(snapshot.metrics);
  const withCustomerRoiSubtitle = (
    rows: WhatsPossibleAtMaturityMetric[],
  ): WhatsPossibleAtMaturityMetric[] =>
    rows.map(metric =>
      metric.type === 'roi'
        ? {
            ...metric,
            subtitle: buildWhatsPossibleAtMaturityRoiSubtitle(customer.name),
          }
        : metric,
    );

  return {
    ...mockBase,
    ...buildWhatsPossibleAtMaturityCopy(customer.name),
    ...snapshot,
    headline: buildWhatsPossibleAtMaturityHeadline(customer.name),
    source: buildWhatsPossibleAtMaturitySource(customer.name),
    metrics:
      metrics.length > 0
        ? withCustomerRoiSubtitle(metrics)
        : buildWhatsPossibleAtMaturityMetrics(customer.name),
    fieldSources: {
      metrics: isBenchmark ? 'benchmark' : 'mock',
      headline: isBenchmark ? 'benchmark' : 'mock',
      summary: isBenchmark ? 'benchmark' : 'mock',
      source: isBenchmark ? 'benchmark' : 'mock',
    },
    snapshotSource: isBenchmark
      ? WHATS_POSSIBLE_AT_MATURITY_BENCHMARK_SOURCE
      : undefined,
  };
};
